import { readCurrencyFragment } from "@opensea/app-kit"
import { graphql, useQuery } from "@opensea/graphql"
import { FlexColumn, List } from "@opensea/ui-kit"
import { useTranslations } from "use-intl"
import { useBalances } from "../../../../../providers/BalancesProvider"
import type { Address } from "../../../../../types"
import {
  ViewWalletPortfolioValue,
  ViewWalletPortfolioValueSkeleton,
} from "../../ViewWalletPortfolioValue"
import { ViewWalletPortfolioValueFragment } from "../../ViewWalletPortfolioValue.fragment"
import { CryptoTabActions } from "./CryptoTabActions"
import {
  CurrencyBalanceItem,
  CurrencyBalanceItemSkeleton,
} from "./CurrencyBalanceItem"

export const CryptoTabQuery = graphql(
  `
    query CryptoTabQuery($address: Address!) {
      profileByAddress(address: $address) {
        __typename
        ... on Profile {
          portfolioSummary {
            estimatedTokenValue {
              ...ViewWalletPortfolioValue
            }
          }
        }
      }
    }
  `,
  [ViewWalletPortfolioValueFragment],
)

export type CryptoTabProps = {
  address: Address
  isProfileOwner: boolean
}

export function CryptoTab({ address, isProfileOwner }: CryptoTabProps) {
  const t = useTranslations("wallet.CryptoTab")
  const { balances } = useBalances()
  const [{ data }] = useQuery({
    query: CryptoTabQuery,
    variables: {
      address,
    },
  })

  if (
    !data?.profileByAddress ||
    data.profileByAddress.__typename !== "Profile"
  ) {
    return <>{t("errorMessage")}</>
  }

  return (
    <FlexColumn className="gap-6 px-4 lg:px-6">
      <ViewWalletPortfolioValue
        portfolioValue={
          data.profileByAddress.portfolioSummary.estimatedTokenValue
        }
      />
      {isProfileOwner && <CryptoTabActions />}
      <List className="bg-transparent" showBorder={false}>
        {balances.map(balance => {
          return (
            <CurrencyBalanceItem
              currency={readCurrencyFragment(balance.currency)}
              key={balance.id}
              quantity={balance.quantity}
              usdValue={balance.usdValue}
              variant="24h"
              withLink
            />
          )
        })}
      </List>
    </FlexColumn>
  )
}

export function CryptoTabSkeleton({
  isProfileOwner,
}: Pick<CryptoTabProps, "isProfileOwner">) {
  return (
    <FlexColumn className="gap-6 px-4 lg:px-6">
      <ViewWalletPortfolioValueSkeleton />
      {isProfileOwner && <CryptoTabActions />}
      <List className="bg-transparent" showBorder={false}>
        <CurrencyBalanceItemSkeleton />
        <CurrencyBalanceItemSkeleton />
        <CurrencyBalanceItemSkeleton />
        <CurrencyBalanceItemSkeleton />
      </List>
    </FlexColumn>
  )
}
